import { useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { RefObject, useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import * as yup from "yup";

import { InboxContext } from "@components/messaging/InboxProvider";
import useCreateMessage from "@hooks/mutations/useCreateMessage";
import useBooleanToggle from "@hooks/useBooleanToggle";
import useContextSafely from "@hooks/useContextSafely";
import useMyExpert from "@hooks/useMyExpert";
import {
  ExpertRoleCreateDocument,
  ExpertRoleCreateMutation,
  ExpertRoleCreateMutationVariables,
  ExpertRoleRoleEnum,
  GetJourneyForMessagesQuery,
  MessageRecordTypeEnum,
  MessageTypingDocument,
  MessageTypingMutation,
  MessageTypingMutationVariables
} from "@typing/Generated";
import { UploadWithFile } from "@typing/Models";
import { handleMutationResponse } from "@utils/formUtils";
import { isMobileWeb, isNativeApp } from "@utils/platformUtils";

type FormData = {
  body: string;
  directUploadBlobIds?: string[];
};

type Props = {
  chat: GetJourneyForMessagesQuery["journey"]["chats"][number];
  contentRef?: RefObject<HTMLIonContentElement>;
  onMessageCreate: () => void;
};

const useMessageForm = ({ chat, contentRef, onMessageCreate }: Props) => {
  const editorRefresher = useBooleanToggle();
  const intl = useIntl();
  const myExpert = useMyExpert();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);
  const [templateId, setTemplateId] = useState<string | null>(null);

  const { journey, resourceData, setResourceData, setWorksheetData, worksheetData } = useContextSafely(InboxContext);

  const [lastTypingMessage, setLastTypingMessage] = useState<Date | undefined>(undefined);

  const defaultValues: FormData = {
    body: "",
    directUploadBlobIds: []
  };
  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      body: yup
        .string()
        .required(intl.formatMessage({ id: "errors.message.body.blank" }))
        .test({
          exclusive: true,
          message: intl.formatMessage({ id: "errors.message.body.blank" }),
          name: "not-empty-p-tags",
          test: value => value !== "<p></p>"
        }),
      directUploadBlobIds: yup.array().defined().of(yup.string().defined())
    })
    .required();

  const form = useForm<FormData>({
    defaultValues,
    mode: "all",
    resolver: yupResolver(schema)
  });
  const typingMessageInterval = 500;
  const uploadsWatch = form.watch("directUploadBlobIds");

  const errors = [];
  if (form.formState.errors.body) errors.push(form.formState.errors.body.message);
  if (baseErrors.length > 0) errors.push(...baseErrors);

  const [messageTyping] = useMutation<MessageTypingMutation, MessageTypingMutationVariables>(MessageTypingDocument);

  const scrollToBottom = useCallback(() => {
    contentRef?.current?.scrollToBottom();
  }, [contentRef]);

  const createMessage = useCreateMessage({ chat, journeyId: journey.id, onMessageCreate });

  const [createExpertRole, { client }] = useMutation<ExpertRoleCreateMutation, ExpertRoleCreateMutationVariables>(
    ExpertRoleCreateDocument,
    {
      update: (_store, { data }) => {
        if (data?.expertRoleCreate?.expertRole) {
          client.resetStore();
        }
      }
    }
  );

  const bodyWatch = form.watch("body");

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);
    scrollToBottom();

    return createMessage({
      body: form.getValues().body,
      chatId: chat.id,
      directUploadBlobIds: form.getValues().directUploadBlobIds,
      messageTemplateId: templateId,
      recordId: resourceData?.id ?? worksheetData?.id,
      recordType: resourceData
        ? MessageRecordTypeEnum.RESOURCE
        : worksheetData
          ? MessageRecordTypeEnum.WORKSHEET
          : undefined
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response?.messageCreate,
        intl,
        onSuccess: async () => {
          if (
            response?.messageCreate?.message &&
            myExpert &&
            !journey.expertRoles?.find(role => role.expert.id === myExpert.id)
          ) {
            createExpertRole({
              variables: {
                expertId: myExpert?.id,
                journeyId: journey.id,
                role: ExpertRoleRoleEnum.TEAM_MEMBER
              }
            });
          }
          form.reset();
          editorRefresher.toggle();
          setTimeout(scrollToBottom, 350);
        },
        setBaseErrors,
        setError: form.setError,
        txBase: "errors.message"
      });

      setResourceData(undefined);
      setWorksheetData(undefined);
    });
  }, [
    chat.id,
    createExpertRole,
    createMessage,
    editorRefresher,
    form,
    intl,
    journey.expertRoles,
    journey.id,
    myExpert,
    resourceData,
    scrollToBottom,
    templateId,
    setResourceData,
    setWorksheetData,
    worksheetData
  ]);

  const placeholder =
    isMobileWeb() || isNativeApp()
      ? intl.formatMessage({ id: "forms.messageCreate.bodyPlaceholder.mobile" })
      : intl.formatMessage({ id: "forms.messageCreate.bodyPlaceholder.desktop" });

  const handleUploadsChange = useCallback(
    (uploads: UploadWithFile[]) => {
      const blobIds = uploads.map(upload => upload.upload.signedBlobId).filter(Boolean);
      form.setValue("directUploadBlobIds", blobIds);
    },
    [form]
  );

  useEffect(() => {
    if (!lastTypingMessage || lastTypingMessage.getTime() < new Date().getTime() - typingMessageInterval) {
      // This isn't amazing, but I can't figure out how else to make it not indicate that the user
      // is typing when the component is initially rendered.
      if (lastTypingMessage) {
        messageTyping({ variables: { chatId: chat.id } });
      }
      setLastTypingMessage(new Date());
    }
  }, [bodyWatch, chat.id, journey, lastTypingMessage, messageTyping]);

  return {
    baseErrors,
    editorRefresher,
    errors,
    form,
    handleUploadsChange,
    onSubmit,
    placeholder,
    setTemplateId,
    uploadsWatch
  };
};

export default useMessageForm;
